import type { FunctionComponent } from 'react';
import { FormattedMessage, useIntl, type MessageDescriptor } from 'react-intl';
import CheckMarkIcon from '@atlaskit/icon/core/check-mark';
import { Box, Inline, Text, xcss } from '@atlaskit/primitives';
import { useMcpContext } from '../McpContextProvider';
import { mcpStepsMessages } from './messages';

const CIRCLE_SIZE = '24px';

const circleStyles = xcss({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  boxSizing: 'border-box',
  width: CIRCLE_SIZE,
  height: CIRCLE_SIZE,
  borderRadius: 'radius.full',
  borderWidth: 'border.width',
  borderStyle: 'solid',
  borderColor: 'color.border',
});

const activeCircleStyles = xcss({
  backgroundColor: 'color.background.accent.gray.subtler',
});

const listStyles = xcss({
  display: 'flex',
  flexDirection: 'column',
  margin: 'space.0',
  padding: 'space.0',
  listStyle: 'none',
});

// Centres the line under the number circle, which is as wide.
const connectorStyles = xcss({
  display: 'flex',
  boxSizing: 'border-box',
  width: CIRCLE_SIZE,
  height: CIRCLE_SIZE,
  justifyContent: 'center',
});

const connectorLineStyles = xcss({
  height: '100%',
  borderLeftWidth: 'border.width',
  borderLeftStyle: 'solid',
  borderLeftColor: 'color.border',
});

type StepProps = {
  index: number;
  label: MessageDescriptor;
  isActive: boolean;
  isComplete?: boolean;
};

const stepColor = (isActive: boolean, isComplete: boolean) => {
  if (isActive) {
    return 'color.text';
  }
  return isComplete ? 'color.text.subtlest' : 'color.text.disabled';
};

const Step: FunctionComponent<StepProps> = ({
  index,
  label,
  isActive,
  isComplete = false,
}) => {
  const color = stepColor(isActive, isComplete);
  return (
    <Inline space="space.100" alignBlock="center">
      <Box xcss={[circleStyles, isActive && activeCircleStyles]}>
        {isComplete ? (
          <CheckMarkIcon label="" size="small" color="currentColor" />
        ) : (
          <Text size="small" weight="bold" color={color}>
            {index}
          </Text>
        )}
      </Box>
      <Text weight="medium" color={color}>
        <FormattedMessage {...label} />
      </Text>
    </Inline>
  );
};

/**
 * Tracks the two-step MCP consent: selecting sites/workspaces, then reviewing
 * the permissions. Rendered only while the two-step consent gate has the flow
 * in two steps (see MCP.tsx).
 */
const McpSteps: FunctionComponent = () => {
  const { formatMessage } = useIntl();
  const { consentStep } = useMcpContext();

  return (
    <Box
      as="ol"
      aria-label={formatMessage(mcpStepsMessages.ariaLabel)}
      xcss={listStyles}
    >
      <li aria-current={consentStep === 'select' ? 'step' : undefined}>
        <Step
          index={1}
          label={mcpStepsMessages.selectStep}
          isActive={consentStep === 'select'}
          isComplete={consentStep === 'review'}
        />
        <Box aria-hidden xcss={connectorStyles}>
          <Box xcss={connectorLineStyles} />
        </Box>
      </li>
      <li aria-current={consentStep === 'review' ? 'step' : undefined}>
        <Step
          index={2}
          label={mcpStepsMessages.reviewStep}
          isActive={consentStep === 'review'}
        />
      </li>
    </Box>
  );
};

export default McpSteps;
